//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import * as collectionsApi from '@apis/collections/collectionsApi';
import { CollectionType } from '@apis/collections/CollectionType';
import * as gatewayFeedApi from '@apis/gatewayFeedApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import {
  getStateName,
  navigateToState,
} from '@react-bridge/wrappers/navigation';
import {
  NUM_ITEMS_PER_PAGE,
  PRODUCT_ERROR_FILTER_VALUE,
  TAGGING_FEED_ACTION_TYPES,
} from '@tagging/constants/taggingConstants';
import {
  BulkPublishableTaggingFeedType,
  IItemDetailsModalItem,
  ITaggingFeedItem,
  TaggingFeedCounts,
  TaggingFeedType,
  TaggingGridLayout,
} from '@tagging/models/taggingModels';
import { createAction } from '@utils/actions';
import { ReduxDispatch } from 'app/curalate.store';
import { getNumberOfPages } from '../selectors/getNumberOfPages';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';

// Async method with side effects. This will update the page query
// parameter in the route but will not trigger a reload. It updates
// the param in-place, and so changes to this value will not
// impact history.
const updatePageNumberInRoute = (pageNumber: number) => {
  // Pages are zero-index, so add one for the readable version
  navigateToState(
    getStateName(),
    { page: pageNumber + 1 },
    {
      location: 'replace',
      reload: false,
      notify: false,
    },
  );
};

function requestFeed(feedType: TaggingFeedType) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.REQUEST_FEED,
    feedType,
  });
}

function receiveFeedSucceeded(feed: ITaggingFeedItem[], totalCount: number) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.RECEIVE_FEED_SUCCEEDED,
    feed,
    totalCount,
  });
}

function receiveFeedFailed() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.RECEIVE_FEED_FAILED,
  });
}

function requestFeedCounts() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.REQUEST_FEED_COUNTS,
  });
}

function receiveFeedCountsSucceeded(feedCounts: TaggingFeedCounts) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.RECEIVE_FEED_COUNTS_SUCCEEDED,
    feedCounts,
  });
}

function receiveFeedCountsFailed() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.RECEIVE_FEED_COUNTS_FAILED,
  });
}

function setGridLayoutValue(layout: TaggingGridLayout) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_GRID_LAYOUT_VALUE,
    layout,
  });
}

function openPublishAllModal() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.OPEN_PUBLISH_ALL_MODAL,
  });
}

function closePublishAllModal() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.CLOSE_PUBLISH_ALL_MODAL,
  });
}

function openItemDetailsModal(item: IItemDetailsModalItem) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.OPEN_ITEM_DETAILS_MODAL,
    item,
  });
}

function setItemDetailsModalSelectedItem(item: IItemDetailsModalItem) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_ITEM_DETAILS_MODAL_SELECTED_ITEM,
    item,
  });
}

function closeItemDetailsModal() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.CLOSE_ITEM_DETAILS_MODAL,
  });
}

function publishAllItems() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ALL_ITEMS,
  });
}

function publishAllItemsSucceeded() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ALL_ITEMS_SUCCEEDED,
  });
}

function publishAllItemsFailed() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ALL_ITEMS_FAILED,
  });
}

function publishItems() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ITEMS,
  });
}

function publishItemsSucceeded(publishedItemsCount: number) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ITEMS_SUCCEEDED,
    publishedItemsCount,
  });
}

function publishItemsFailed() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.PUBLISH_ITEMS_FAILED,
  });
}

function updatePageNumber(pageNumber: number) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.UPDATE_PAGE_NUMBER,
    pageNumber,
  });
}

function removeItems() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.REMOVE_ITEMS,
  });
}

function removeItemsSucceeded(removedItemsCount: number) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.REMOVE_ITEMS_SUCCEEDED,
    removedItemsCount,
  });
}

function removeItemsFailed() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.REMOVE_ITEMS_FAILED,
  });
}

function forceUpdateItems() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.FORCE_UPDATE_ITEMS,
  });
}

function forceUpdateItemsFinished() {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.FORCE_UPDATE_ITEMS_FINISHED,
  });
}

function setFeedGenerationState(isGeneratingFeed: boolean) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_FEED_GENERATION_STATE,
    isGeneratingFeed,
  });
}

function setFetchingInitialFeedGenerationStatus(
  isFetchingInitialFeedGenerationStatus: boolean,
) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_FEED_GENERATION_LOOKUP_STATUS,
    isFetchingInitialFeedGenerationStatus,
  });
}

function setProductErrorFilterValue(
  productErrorFilterValue: PRODUCT_ERROR_FILTER_VALUE,
) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_PRODUCT_ERROR_FILTER_VALUE,
    productErrorFilterValue,
  });
}

function setSearchTextValue(searchTextValue: string) {
  return createAction({
    type: TAGGING_FEED_ACTION_TYPES.SET_SEARCH_TEXT_VALUE,
    searchTextValue,
  });
}

export type TaggingFeedActionType = ReturnType<
  | typeof requestFeed
  | typeof receiveFeedSucceeded
  | typeof receiveFeedFailed
  | typeof requestFeedCounts
  | typeof receiveFeedCountsSucceeded
  | typeof receiveFeedCountsFailed
  | typeof setGridLayoutValue
  | typeof openPublishAllModal
  | typeof closePublishAllModal
  | typeof openItemDetailsModal
  | typeof closeItemDetailsModal
  | typeof updatePageNumber
  | typeof publishAllItems
  | typeof publishAllItemsSucceeded
  | typeof publishAllItemsFailed
  | typeof publishItems
  | typeof publishItemsSucceeded
  | typeof publishItemsFailed
  | typeof removeItems
  | typeof removeItemsSucceeded
  | typeof removeItemsFailed
  | typeof forceUpdateItems
  | typeof forceUpdateItemsFinished
  | typeof setFeedGenerationState
  | typeof setFetchingInitialFeedGenerationStatus
  | typeof setItemDetailsModalSelectedItem
  | typeof setProductErrorFilterValue
  | typeof setSearchTextValue
>;

const navigateToLastPageIfOutOfBounds =
  (desiredOffset: number) =>
  (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    // If there are items in the feed, but the offset is too high to return
    // any, navigate the user to what is now the last page. This can occur
    // when a user is on the last page of content and they moderate all items
    // out of it. What was previously the last page of items is now an invalid
    // page number, so calculate the correct zero-indexed last page and navigate
    // the user their instead.
    const { totalFeedItemCount } = getState().tagging.feedReducer;
    if (desiredOffset !== 0 && totalFeedItemCount <= desiredOffset) {
      const lastPageIndex = getNumberOfPages(getState()) - 1;
      dispatch(updateFeedPage(lastPageIndex));
    }
  };

const getFeed =
  (feedType: TaggingFeedType, pageNumber?: number) =>
  async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ): Promise<void> => {
    dispatch(requestFeed(feedType));
    dispatch(getFeedCounts());

    if (pageNumber >= 0) {
      dispatch(updatePageNumber(pageNumber));
    }

    const offset = pageNumber * NUM_ITEMS_PER_PAGE;

    const { productErrorFilterValue, searchTextValue } =
      getState().tagging.feedReducer;

    try {
      const response = await gatewayFeedApi.getFeedItems(
        feedType,
        productErrorFilterValue,
        offset || 0,
        NUM_ITEMS_PER_PAGE,
        searchTextValue,
      );
      if (response && response.data) {
        dispatch(
          receiveFeedSucceeded(response.data.items, response.data.totalCount),
        );
        dispatch(navigateToLastPageIfOutOfBounds(offset));
        dispatch(refreshItemDetailsModal());
      }
    } catch (err) {
      // If the request for feed items returned an error, check to see if the client
      // has any streams configured. If they don't, then we shouldn't show an error
      // warning them that something went wrong.
      const assetFeedsStreams = await collectionsApi.getStreams(
        CollectionType.GOOGLE_FEED_STREAM,
      );
      if (assetFeedsStreams && assetFeedsStreams.length > 0) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: `Something went wrong and we couldn't load your feed. Please try again.`,
            type: ToastType.ERROR,
          }),
        );
      }
      dispatch(receiveFeedFailed());
    }
  };

const refreshItemDetailsModal =
  () =>
  async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ): Promise<void> => {
    const { itemDetailsModalSelectedItem, feed } =
      getState().tagging.feedReducer;
    if (itemDetailsModalSelectedItem) {
      const selectedItemId = itemDetailsModalSelectedItem.item.networkItem.id;
      const updatedSelectedItem = feed.find(
        (item) => item.networkItem.id === selectedItemId,
      );
      if (!updatedSelectedItem) {
        dispatch(closeItemDetailsModal());
      } else {
        dispatch(openAndLoadItemDetailsModal(updatedSelectedItem));
      }
    }
  };

const refreshFeed =
  () =>
  async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ): Promise<void> => {
    const { taggingFeedType, currentPageNumber } =
      getState().tagging.feedReducer;
    await dispatch(getFeed(taggingFeedType, currentPageNumber));
  };

const updateFeedPage =
  (pageNumber: number) =>
  async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    dispatch(updatePageNumber(pageNumber));
    // Side effect, update the `page` query param without changing the state
    updatePageNumberInRoute(pageNumber);
    const theFeedType = getState().tagging.feedReducer.taggingFeedType;
    await dispatch(getFeed(theFeedType, pageNumber));
  };

const getFeedCounts =
  () =>
  async (dispatch: ReduxDispatch): Promise<void> => {
    dispatch(requestFeedCounts());
    try {
      const response = await gatewayFeedApi.getFeedCounts();
      if (response && response.data) {
        dispatch(receiveFeedCountsSucceeded(response.data.itemCounts));
      }
    } catch (err) {
      dispatch(receiveFeedCountsFailed());
    }
  };

const openAndLoadItemDetailsModal =
  (selectedFeedItem: ITaggingFeedItem) =>
  async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ): Promise<void> => {
    // Immediately open the item details modal, then surface social data once it's loaded.
    dispatch(
      openItemDetailsModal({
        item: selectedFeedItem,
      }),
    );

    try {
      const clientId = ClientId.fromString(getState().session.client.id);
      const cuid = Cuid.buildFromNetworkItem(
        selectedFeedItem.networkItem,
        clientId,
      );
      const response = await getMediaItemMetadata(cuid);
      if (response && response.socialDetails) {
        const selectedItem = {
          item: selectedFeedItem,
          socialMetadata: response.socialDetails,
        };
        dispatch(setItemDetailsModalSelectedItem(selectedItem));
      }
    } catch (err) {
      // If fetching social details fails, swallow the error
    }
  };

export const taggingFeedActions = {
  getFeed,
  refreshFeed,
  setGridLayoutValue,
  openPublishAllModal,
  closePublishAllModal,
  openAndLoadItemDetailsModal,
  closeItemDetailsModal,
  updateFeedPage,
  getFeedCounts,
  setProductErrorFilterValue,
  setSearchTextValue,
  publishAllItems:
    (feedType: BulkPublishableTaggingFeedType) =>
    async (dispatch: ReduxDispatch): Promise<void> => {
      dispatch(publishAllItems());
      try {
        await gatewayFeedApi.publishAllFeedItems(feedType);
        dispatch(publishAllItemsSucceeded());
        dispatch(taggingFeedActions.generateFeed());
      } catch (err) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: `Something went wrong and we couldn't publish your items. Please try again.`,
            type: ToastType.ERROR,
          }),
        );
        dispatch(publishAllItemsFailed());
      }
    },
  publishItems:
    (cuids: Cuid[]) =>
    async (
      dispatch: ReduxDispatch,
      getState: () => ICuralateStore,
    ): Promise<number> => {
      dispatch(publishItems());
      try {
        const feedType = getState().tagging.feedReducer.taggingFeedType;
        const response = await gatewayFeedApi.publishFeedItems(cuids, feedType);
        if (response && response.data) {
          dispatch(publishItemsSucceeded(response.data.totalCount));
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: `Published ${response.data.totalCount} items successfully`,
              type: ToastType.SUCCESS,
            }),
          );
          dispatch(refreshFeed());
          return response.data.totalCount;
        }
      } catch (err) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: `Something went wrong and we couldn't publish your items. Please try again.`,
            type: ToastType.ERROR,
          }),
        );
        dispatch(publishItemsFailed());
        return null;
      }
    },
  removeItems:
    (cuids: Cuid[]) =>
    async (dispatch: ReduxDispatch): Promise<number> => {
      dispatch(removeItems());
      try {
        const response = await gatewayFeedApi.removeFeedItems(cuids);
        if (response && response.data) {
          dispatch(removeItemsSucceeded(response.data.totalCount));
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: `Removed ${response.data.totalCount} items successfully`,
              type: ToastType.SUCCESS,
            }),
          );
          dispatch(refreshFeed());
          return response.data.totalCount;
        }
        return 0;
      } catch (err) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: `Something went wrong and we couldn't remove your items. Please try again.`,
            type: ToastType.ERROR,
          }),
        );
        dispatch(removeItemsFailed());
        return null;
      }
    },
  generateFeed: () => async (dispatch: ReduxDispatch) => {
    navigateToState('tagging.pending');
    await gatewayFeedApi.startFeedGeneration();
    dispatch(setFeedGenerationState(true));
    dispatch(taggingFeedActions.pollForJobStatus(true));
  },
  determineFeedGenerationState: () => (dispatch: ReduxDispatch) => {
    dispatch(setFetchingInitialFeedGenerationStatus(true));

    dispatch(taggingFeedActions.pollForJobStatus());
  },
  pollForJobStatus:
    (previousJobStatusWasRunning: boolean = false) =>
    async (dispatch: ReduxDispatch) => {
      const response = await gatewayFeedApi.getSettings();

      if ('data' in response) {
        const latestJobStatus = response.data.jobStatus;

        if (!latestJobStatus || !latestJobStatus.isRunning) {
          dispatch(setFetchingInitialFeedGenerationStatus(false));

          if (previousJobStatusWasRunning) {
            // If this isn't the initial page load, and the job was previously running,
            // refresh the feed and send a toast when the job completes
            await dispatch(taggingFeedActions.refreshFeed());

            dispatch(
              toastNotificationsActions.addToastNotification({
                title: 'Success!',
                message: 'Your feed has been updated.',
                type: ToastType.SUCCESS,
              }),
            );
          }

          dispatch(setFeedGenerationState(false));
        } else {
          dispatch(setFeedGenerationState(true));
          // If job is still running, check againin 10 seconds
          setTimeout(
            () =>
              dispatch(
                taggingFeedActions.pollForJobStatus(latestJobStatus?.isRunning),
              ),
            10000,
          );
        }
      }
    },
  forceUpdateItems: (cuids: Cuid[]) => async (dispatch: ReduxDispatch) => {
    dispatch(forceUpdateItems());
    try {
      const response = await gatewayFeedApi.forceUpdateItems(cuids);
      if (response && response.data) {
        dispatch(forceUpdateItemsFinished());
        dispatch(refreshFeed());
        return response.data.updatedCount;
      }
    } catch (err) {
      // Force updating items is best effort. If it fails we won't
      // surface that to the user
      dispatch(forceUpdateItemsFinished());
    }
  },
};
